import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Star, Utensils } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState } from "react";
const Services = () => {
  const isMobile = useIsMobile();
  const [expandedPlans, setExpandedPlans] = useState<Set<string>>(new Set());
  const plans = [{
    id: "smooth-landing",
    name: "Smooth Landing",
    price: 328.90,
    tagline: "Start smart. Settle smoothly.",
    perfectFor: "Independent students who want a safe, guided start in Australia.",
    bookingFee: "₹6,000 INR (≈25%)",
    popular: false,
    features: ["✈️ Melbourne airport pickup on arrival", "📱 Australian SIM setup + mobile plan guidance", "🏦 TFN & bank account assistance", "🏠 Short-term accommodation guidance (up to 1 week)", "🧾 OSHC explanation - how to claim and use it", "🚌 Public transport (Myki) setup guidance", "💬 24/7 support chat for 7 days after arrival", "👨‍👩‍👦 Parent Update Service (Free) - we'll notify your parents about your safe arrival and settlement progress personally", "⚙️ Add-ons available (meals, India airport drop-off)"],
    outcome: "Arrive stress-free, get your essentials sorted, and start your new life with confidence."
  }, {
    id: "settle-smart",
    name: "Settle Smart",
    price: 548.90,
    tagline: "Everything you need to feel at home.",
    perfectFor: "Students & parents who want complete peace of mind from touchdown to work-ready.",
    bookingFee: "₹9,999 INR (≈20%)",
    popular: true,
    features: ["Everything in Smooth Landing, plus:", "🚗 Help renting a low-budget car (Uber/DoorDash ready)", "👨‍💻 Job support & guidance to start earning from 2nd week after arrival", "🧾 Superannuation setup + explanation", "🏠 Permanent accommodation assistance post-arrival (We'll help you find verified, safe, and affordable long-term options once you're in Australia, including bond process, inspections & tenancy setup)", "💳 Bank, TFN & MyGov follow-ups", "🧭 OSHC claim walk-through", "🤝 Dedicated Ace Anchor manager for 2 weeks post-arrival", "👨‍👩‍👦 Parent Update Service (Free) - we'll notify your parents about your safe arrival and settlement progress personally", "⚙️ Add-ons available (meals, India airport drop-off)"],
    outcome: "Land, settle, and start earning within your first two weeks - with complete family-like support and guidance through every step."
  }];
  const mealPlans = [{
    days: 3,
    price: 75.90,
    name: "Taste of Home Starter Pack",
    description: "ideal for short stays"
  }, {
    days: 5,
    price: 108.90,
    name: "Comfort Pack",
    description: "most popular choice for new arrivals",
    popular: true
  }, {
    days: 7,
    price: 141.90,
    name: "Full Week Pack",
    description: "complete settlement comfort"
  }];
  return <section className="py-24 bg-muted/30">
      <div className="container mx-auto px-4">
        <header className="max-w-3xl mx-auto text-center space-y-4 mb-16 animate-fade-in">
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground">
            One-Stop Student{" "}
            <span className="text-secondary">Concierge Services</span>
          </h2>
          <p className="text-xl text-muted-foreground">
            Choose the plan that fits your needs. Every plan is designed to remove stress and add confidence.
          </p>
        </header>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
          {plans.map((plan, index) => <div key={plan.name} className={`relative bg-card rounded-3xl p-8 shadow-card hover:shadow-xl hover:-translate-y-2 transition-all duration-500 animate-fade-in ${plan.popular ? "ring-2 ring-accent md:scale-105" : ""}`} style={{
          animationDelay: `${index * 0.1}s`
        }}>
              {plan.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-accent text-accent-foreground rounded-full text-sm font-semibold flex items-center gap-1">
                  <Star className="w-4 h-4 fill-current" />
                  Most Popular
                </div>}

              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-foreground mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-muted-foreground italic mb-3">
                    {plan.tagline}
                  </p>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-4xl font-bold text-foreground">
                      ${plan.price}
                    </span>
                    <span className="text-muted-foreground">AUD</span>
                  </div>
                  <p className="text-sm text-muted-foreground/60 mb-1">
                    ≈ ₹{plan.id === "smooth-landing" ? "18,500" : "31,000"} INR
                  </p>
                  <p className="text-xs text-muted-foreground mb-2">
                    (incl. 10% GST)
                  </p>
                  
                </div>

                <div>
                  <p className="text-sm font-semibold text-foreground mb-2">
                    Perfect for: <span className="font-normal text-muted-foreground">{plan.perfectFor}</span>
                  </p>
                </div>

                <ul className="space-y-3">
                  {plan.features.map(feature => <li key={feature} className="flex gap-3">
                      <Check className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">
                        {feature}
                      </span>
                    </li>)}
                </ul>

                <div className="pt-4 border-t border-border">
                  <p className="text-sm font-semibold text-foreground mb-1">Outcome:</p>
                  <p className="text-sm text-muted-foreground">{plan.outcome}</p>
                </div>

                <Button className="w-full" variant={plan.popular ? "accent" : "default"} size="lg" asChild>
                  <Link to={`/booking?plan=${plan.id}`}>
                    {plan.popular ? "💛 Reserve Settle Smart" : "🔵 Book Smooth Landing"} - ${plan.price} AUD (incl. GST)
                  </Link>
                </Button>
              </div>
            </div>)}
        </div>

        {/* Optional Add-Ons Section */}
        <div className="max-w-5xl mx-auto mb-16 animate-fade-in" style={{
        animationDelay: "0.3s"
      }}>
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-foreground mb-3">
              Optional Add-Ons
            </h3>
              <p className="text-lg text-muted-foreground mb-4">Available for Both Plans</p>
              <div className="flex items-center justify-center gap-6 text-xl">
                <span>1️⃣ Indian meals 🍱</span>
                <span>2️⃣  Airport drop-off 🚕</span>
              </div>
          </div>

          <Card className="bg-gradient-to-br from-card to-muted/30 border-2">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-2xl">Indian Veg Meals – Fresh, Home-Cooked Comfort</CardTitle>
              <CardDescription className="text-base">
                Enjoy freshly prepared vegetarian Indian meals delivered to your accommodation during your first week in Australia.
                <br />
                <span className="italic text-foreground font-medium">Because every journey deserves a taste of home.</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                {mealPlans.map(meal => <div key={meal.days} className={`relative bg-card rounded-2xl p-6 shadow-md transition-all hover:-translate-y-1 ${meal.popular ? "ring-2 ring-accent scale-105" : ""}`}>
                    {meal.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-accent text-accent-foreground rounded-full text-xs font-semibold">
                        Most Popular
                      </div>}
                    <div className="text-center space-y-3">
                      <div className="text-4xl font-bold text-foreground">
                        {meal.days === 3 ? "🥣" : meal.days === 5 ? "🍛" : "🍲"}
                      </div>
                      <div>
                        <div className="text-3xl font-bold text-foreground">${meal.price}</div>
                        <div className="text-xs text-muted-foreground">(incl. 10% GST)</div>
                        <div className="text-sm text-muted-foreground">{meal.days} Days</div>
                      </div>
                      <div>
                        <div className="font-semibold text-foreground text-sm mb-1">"{meal.name}"</div>
                        <div className="text-xs text-muted-foreground">{meal.description}</div>
                      </div>
                    </div>
                  </div>)}
              </div>

              <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-secondary" />
                  <span>100% vegetarian</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-secondary" />
                  <span>Hygienically packed</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-secondary" />
                  <span>Delivered fresh</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Simple 2-Step Booking */}
        <div className="max-w-3xl mx-auto mb-12 animate-fade-in" style={{
        animationDelay: "0.4s"
      }}>
          <Card className="bg-gradient-to-br from-accent/10 to-secondary/10 border-2 border-accent/30">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Simple 2-Step Booking</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-accent text-accent-foreground rounded-full flex items-center justify-center font-bold text-lg">
                  1
                </div>
                <div>
                  <p className="text-foreground font-semibold">Pay a small booking amount in India</p>
                  <p className="text-sm text-muted-foreground mt-1">Non-refundable booking amount to secure your spot</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-secondary text-secondary-foreground rounded-full flex items-center justify-center font-bold text-lg">
                  2
                </div>
                <div>
                  <p className="text-foreground font-semibold">Pay the remaining balance in Australia after arrival</p>
                  <p className="text-sm text-muted-foreground mt-1">Payable by card, cash, or payment link</p>
                </div>
              </div>
              <div className="pt-4 border-t border-border/50 space-y-2">
                <p className="text-sm font-semibold text-foreground">✅ Transparent. Safe. Commitment-based.</p>
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <p className="text-sm text-foreground font-medium">📋 Important Payment Information:</p>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• All prices are GST inclusive</li>
                    <li>• A non-refundable booking amount is required to secure your spot</li>
                    <li>• Balance is payable in Australia on arrival by card, cash, or payment link</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
         </div>

        {/* Disclaimer */}
        <div className="max-w-4xl mx-auto mt-16 animate-fade-in" style={{
        animationDelay: "0.5s"
      }}>
          <Card className="bg-muted/50 border-muted">
            <CardContent className="pt-6">
              <h4 className="font-semibold text-foreground mb-3 text-center">⚖️ Disclaimer</h4>
                  <p className="text-sm text-muted-foreground text-center leading-relaxed">
                Ace Anchor is a student concierge and settlement assistance service.
                We are not an education consultant, migration agent, or visa advisor, and we do not offer immigration, enrollment, or legal guidance.
                All our services are focused purely on post-arrival support, comfort, and integration for international students in Australia.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Book Your Spot CTA */}
        <div className="mt-12 text-center animate-fade-in" style={{
        animationDelay: "0.6s"
      }}>
          <Card className="max-w-2xl mx-auto bg-gradient-to-br from-secondary/10 to-accent/10 border-2 border-secondary/20">
            <CardContent className="p-8 space-y-4">
              <h3 className="text-2xl font-bold text-foreground">
                Still Have Questions?
              </h3>
              <p className="text-lg text-muted-foreground">
                Book a free consultation call with our founders to discuss your needs and get personalized guidance.
              </p>
              <Button size="lg" variant="secondary" asChild className="hover:scale-105 transition-transform duration-300">
                <Link to="/consultation">
                  Book Your Spot
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
};
export default Services;